import { useEffect, useRef } from 'react'
import { favouriteTeams, plTeams } from '../mockTeams'
import { teamDisplayName, type TeamId } from '../teams'
import { getClubBanner, getClubAbbr } from '../clubBanners'
import ModalBackdrop from './ModalBackdrop'
import ModalHeader from './ModalHeader'
import type { Game } from '../types'
import './QuestPickerSheet.css'

interface QuestPickerSheetProps {
  /** Which list to render — team pick's grid or match pick's fixture list. */
  type: 'team' | 'match'
  /** The gameweek's fixtures — source of the fixture list, and of which teams
   * are actually playing (team grid only offers those, per the brief).
   * Optional so the favourite-team picker can reuse the same
   * grid without a gameweek constraint — see `allTeams`. */
  games?: Game[]
  /** When set, the team grid offers every PL team regardless of who's playing
   * this gameweek — used by the Profile favourite-team picker, which isn't
   * scoped to a gameweek. Ignored in match mode. */
  allTeams?: boolean
  /** Current picks, so a re-opened sheet (tapping "Change") can highlight
   * the existing selection. */
  selectedTeam?: TeamId
  selectedGameId?: string
  onSelectTeam?: (team: TeamId) => void
  onSelectGame?: (game: Game) => void
  onClose: () => void
}

function formatKickoffTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>('button:not(:disabled)')
  )
}

/** The picker's own content, inside `ModalBackdrop`'s panel. Split out purely so
 *  the focus trap can close through the backdrop's `close` —
 *  that's a render-prop argument, and an effect needs it in scope. */
function QuestPickerPanel({
  type,
  games,
  allTeams,
  selectedTeam,
  selectedGameId,
  onSelectTeam,
  onSelectGame,
  close,
}: Omit<QuestPickerSheetProps, 'onClose' | 'games' | 'allTeams'> & {
  games: Game[]
  allTeams: boolean
  close: (after?: () => void) => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  // The trap's keydown listener is bound once, so it must not close over a
  // stale `close` from the first render.
  const closeRef = useRef(close)
  useEffect(() => {
    closeRef.current = close
  })

  // Focus trap + initial focus — ModalBackdrop has no keyboard plumbing to
  // reuse (it's a scrim with click-to-close and the exit animation), so the
  // picker still owns this, now pointed at its own panel.
  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    // Land on the first *option*, not the header's ✕ — the ✕ is now the panel's
    // first focusable element, but opening a picker should put you on the list.
    const firstOption = panel.querySelector<HTMLElement>(
      '.quest-picker-sheet__grid-item, .quest-picker-sheet__fixture-row'
    )
    ;(firstOption ?? focusableElements(panel)[0])?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const elements = focusableElements(panel)
      if (elements.length === 0) return
      const firstEl = elements[0]
      const lastEl = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault()
        lastEl.focus()
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Real PL teams playing this gameweek only — a team not in any fixture
  // can't be picked (the brief's "unavailable teams should not be seen").
  // `allTeams` (favourite-team picker) opts out of that gameweek filter, and
  // picks from `favouriteTeams` — supportable clubs, which includes the ones
  // not competing this season. The filtered branch stays on
  // `plTeams` so those clubs are excluded by construction, not just because
  // they happen to have no fixtures.
  // Compared by team id, so a spelling mismatch ('Manchester City' vs 'Man City', 'Man Utd' vs
  // 'Man United') cannot exist — both
  // sides are the same canonical id.
  const availableTeams = allTeams
    ? favouriteTeams
    : plTeams.filter((team) =>
        games.some((game) => game.homeTeam === team.id || game.awayTeam === team.id)
      )
  const sortedGames = [...games].sort(
    (a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime()
  )

  const title = type === 'team' ? 'Pick a team' : 'Pick a match'

  return (
    <div
      ref={panelRef}
      className="quest-picker"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <ModalHeader title={title} onClose={() => close()} />

      {type === 'team' ? (
        <div className="quest-picker-sheet__grid">
          {availableTeams.map((team) => (
            <button
              key={team.id}
              type="button"
              className={`quest-picker-sheet__grid-item${
                team.id === selectedTeam
                  ? ' quest-picker-sheet__grid-item--selected'
                  : ''
              }`}
              onClick={() => close(() => onSelectTeam?.(team.id))}
            >
              <img
                className="quest-picker-sheet__shield"
                src={getClubBanner(team.id) ?? ''}
                alt=""
                aria-hidden="true"
              />
              <span className="quest-picker-sheet__grid-name">{teamDisplayName(team.id)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="quest-picker-sheet__fixture-list">
          {sortedGames.map((game) => (
            <button
              key={game.id}
              type="button"
              className={`quest-picker-sheet__fixture-row${
                game.id === selectedGameId
                  ? ' quest-picker-sheet__fixture-row--selected'
                  : ''
              }`}
              onClick={() => close(() => onSelectGame?.(game))}
            >
              <span className="quest-picker-sheet__fixture-side">
                <img
                  className="quest-picker-sheet__fixture-shield"
                  src={getClubBanner(game.homeTeam) ?? ''}
                  alt=""
                  aria-hidden="true"
                />
                <span className="quest-picker-sheet__fixture-code">
                  {getClubAbbr(game.homeTeam)}
                </span>
              </span>
              <span className="quest-picker-sheet__fixture-time">
                {formatKickoffTime(game.kickoff)}
              </span>
              <span className="quest-picker-sheet__fixture-side">
                <span className="quest-picker-sheet__fixture-code">
                  {getClubAbbr(game.awayTeam)}
                </span>
                <img
                  className="quest-picker-sheet__fixture-shield"
                  src={getClubBanner(game.awayTeam) ?? ''}
                  alt=""
                  aria-hidden="true"
                />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** This was the app's last bespoke overlay — a bottom sheet with
 *  its own scrim, rise animation and exit timer. It's now a plain
 *  `ModalBackdrop` panel like every other modal; only the grid/fixture-list
 *  content is still the picker's own. */
function QuestPickerSheet({
  type,
  games = [],
  allTeams = false,
  selectedTeam,
  selectedGameId,
  onSelectTeam,
  onSelectGame,
  onClose,
}: QuestPickerSheetProps) {
  return (
    <ModalBackdrop onClose={onClose}>
      {(close) => (
        <QuestPickerPanel
          type={type}
          games={games}
          allTeams={allTeams}
          selectedTeam={selectedTeam}
          selectedGameId={selectedGameId}
          onSelectTeam={onSelectTeam}
          onSelectGame={onSelectGame}
          close={close}
        />
      )}
    </ModalBackdrop>
  )
}

export default QuestPickerSheet
